import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import cx from "classnames";
//@ts-ignore
import Pagination from "react-js-pagination";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import {
  AnalyticsTableColumnMeta,
  AnalyticsTableModelPropertyMeta,
  PagedAnalyticsTableDataDto,
} from "../../../types/analytics/tables/AnalyticsTables";
import { AnalyticsLoadingSpinner } from "../AnalyticsLoadingSpinner";
import { AnalyticsDataTableSkeleton } from "./AnalyticsDataTableSkeleton";
import analyticsCustomEndpointApi from "../../../api/analytics/analyticsCustomEndpointApi";
import { useAuth } from "react-oidc-context";
import IAnalyticsDataItem from "../../../types/analytics/IAnalyticsDataItem";
import { DataRowWithEmployeeDetails } from "../../../types/analytics/tables/DataRowWithEmployeeDetails";
import { GroupedJourneys } from "../../../types/analytics/FilterAvailableValues";
import { journeyFilterTitlesByGuid } from "../../../helpers/analytics/journeyFilterHelpers";
import { displayEmployeeDimensionLabel } from "../../../helpers/analytics/displayEmployeeDimensionLabel";
import { FormattedDate } from "../../common";
import { ChartLegendItem } from "../../../types/analytics/AnalyticsWidgetUiDetailsDto";
import { KeyValuePair } from "../../../types/generic";
import TinyLoader from "../../loaders/TinyLoader";
import UserContext from "../../../state/UserContext";
import SearchField from "../../common/SearchField";
import UserProfileHoverCard from "../../common/UserProfileHoverCard";
import { Link } from "react-router-dom";

type AnalyticsDataTableProps = {
  apiUrl: string;
  filters: AnalyticsFilterInputs;
  journeys: GroupedJourneys[];
  legendItems: ChartLegendItem[] | null;
  /** Allows passing of custom params to the API which loads the table data */
  otherApiParams: KeyValuePair<string, string>[] | null;
  /** Actions for the current row selection, shown below the table and above the pager. */
  bulkActions?: React.ReactNode;
  /** When set, the first load shows a table-shaped skeleton with this many data columns instead of the spinner. */
  skeletonColumnCount?: number;
  rowSelection?: {
    selectedEmployeeIds: Set<number>;
    canSelect: (row: DataRowWithEmployeeDetails<IAnalyticsDataItem>) => boolean;
    onToggle: (employeeId: number) => void;
    onSelectPage: (employeeIds: number[], selected: boolean) => void;
  };
  rowAction?: {
    header: string;
    hideWhenNoViewPermission?: boolean;
    forceVisible?: boolean;
    render: (
      row: DataRowWithEmployeeDetails<IAnalyticsDataItem>,
    ) => React.ReactNode;
  };
};

interface TableRowData {
  employeeId: number;
  rawRow: DataRowWithEmployeeDetails<IAnalyticsDataItem>;
  data: TableRowDataCell[];
}

interface TableRowDataCell {
  columnName: string;
  displayValue: React.ReactNode;
  classNames: string | undefined;
}

interface TableSearchPageDetails {
  pageNumber: number;
  searchPhrase: string | null;
}

const getHorizontalAlignmentClassName = (
  column: AnalyticsTableColumnMeta | undefined,
) => {
  if (
    !column ||
    column.dataType === "TRANSLATED-STRING" ||
    column.dataType === "RAW-STRING" ||
    column.dataType === "RAW-STRING-MULTI" ||
    column.dataType === "JOURNEY-NAME"
  )
    return "text-left";

  return "text-center";
};

const getTableCellValue = (
  row: DataRowWithEmployeeDetails<IAnalyticsDataItem>,
  column: AnalyticsTableColumnMeta,
  columns: AnalyticsTableColumnMeta[],
  modelMeta: AnalyticsTableModelPropertyMeta[],
  journeys: KeyValuePair<string, string>[],
  legendItems: ChartLegendItem[] | null,
  isSnapshot: boolean,
) => {
  const matchingCol = columns.find((x) => x.name === column.name);
  if (!matchingCol) return null;

  const cellClassNames = getHorizontalAlignmentClassName(matchingCol);

  let rawDisplayValue: string | Array<string> = "";
  const matchingModelProperty = modelMeta.find(
    (x) => x.columnKey === column.name,
  );
  if (matchingModelProperty) {
    if (matchingModelProperty.objectKey.startsWith("data.")) {
      rawDisplayValue = (row.data as any)[
        matchingModelProperty.objectKey.substring(5)
      ];
    } else {
      rawDisplayValue = (row as any)[matchingModelProperty.objectKey];
    }
  }

  let outputDisplayValue: string | React.ReactNode;
  // If the item maps to a legend item, try to find it so we can transform an enum to a translation/string value
  if (column.mapToLegendItems && legendItems != null) {
    const matchingLegendItem = legendItems.find(
      (x) => x.key === rawDisplayValue,
    );
    if (matchingLegendItem) {
      outputDisplayValue = matchingLegendItem.translatable
        ? t(matchingLegendItem.textValue)
        : matchingLegendItem.textValue;
    }
  } else {
    switch (column.dataType) {
      case "TRANSLATED-STRING":
        outputDisplayValue = displayEmployeeDimensionLabel(
          Array.isArray(rawDisplayValue) ? rawDisplayValue[0] : rawDisplayValue,
          isSnapshot,
          t,
        );
        break;
      case "DATE":
        if (rawDisplayValue) {
          outputDisplayValue = (
            <FormattedDate
              date={new Date(rawDisplayValue as string)}
              displayMode="DATE-ONLY"
            />
          );
        }
        break;
      case "JOURNEY-NAME":
        const matchedJourney = journeys.find((x) => x.key === rawDisplayValue);
        if (matchedJourney) {
          outputDisplayValue = matchedJourney.value;
        }
        break;
      case "RAW-STRING-MULTI":
        outputDisplayValue = (
          <>
            {Array.isArray(rawDisplayValue) &&
              rawDisplayValue.map((x: string, ix: number) => (
                <div key={`${x}_${ix}`}>{x}</div>
              ))}
            {!Array.isArray(rawDisplayValue) && <div>{rawDisplayValue}</div>}
          </>
        );
        break;
      case "HYPERLINK":
        if (rawDisplayValue && !Array.isArray(rawDisplayValue)) {
          outputDisplayValue = (
            <>
              <Link to={rawDisplayValue} target="_blank">
                <span className="py-1 px-4 text-gray-600 bg-white border rounded hover:bg-gray-50">
                  {t("Common.View")}
                </span>
              </Link>
            </>
          );
        }
        break;
      default:
        outputDisplayValue = rawDisplayValue;
        break;
    }
  }

  return {
    displayValue: outputDisplayValue,
    cellClassNames,
  };
};

export const AnalyticsDataTable = ({
  apiUrl,
  filters,
  journeys,
  legendItems,
  otherApiParams,
  bulkActions,
  skeletonColumnCount,
  rowSelection,
  rowAction,
}: AnalyticsDataTableProps) => {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const api = new analyticsCustomEndpointApi(auth.user?.access_token);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageSearchDetails, setPageSearchDetails] =
    useState<TableSearchPageDetails>({ pageNumber: 1, searchPhrase: null });
  const [journeyTitles, setJourneyTitles] = useState<
    KeyValuePair<string, string>[]
  >([]);
  const [modelMeta, setModelMeta] = useState<AnalyticsTableModelPropertyMeta[]>(
    [],
  );
  const [columns, setColumns] = useState<AnalyticsTableColumnMeta[]>([]);
  const pageSize = 10;
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [rowData, setRowData] = useState<TableRowData[]>([]);
  const [hasAnyRowViewPermission, setHasAnyRowViewPermission] = useState(false);
  const isSnapshot = (filters.pointInTimeDate?.trim() ?? "") !== "";

  const hasSearchValue =
    pageSearchDetails.searchPhrase !== null &&
    pageSearchDetails.searchPhrase.trim().length > 0;

  const transformTableData = (
    newRowData: DataRowWithEmployeeDetails<IAnalyticsDataItem>[],
    newColumns: AnalyticsTableColumnMeta[],
    newModelMeta: AnalyticsTableModelPropertyMeta[],
  ) => {
    // Take each row and transform it to a display value
    const tableData = newRowData.map((row) => {
      const newRow: TableRowData = {
        employeeId: row.employeeId,
        rawRow: row,
        data: [],
      };

      newColumns.forEach((col) => {
        const cellValue = getTableCellValue(
          row,
          col,
          newColumns,
          newModelMeta,
          journeyTitles,
          legendItems,
          isSnapshot,
        );
        newRow.data.push({
          columnName: col.name,
          displayValue: cellValue?.displayValue,
          classNames: cellValue?.cellClassNames,
        });
      });
      return newRow;
    });

    // Remove any columns which don't have any data in them
    const nonEmptyColumns: AnalyticsTableColumnMeta[] = [];
    newColumns.forEach((col) => {
      const nonEmptyCell = tableData.find((row) =>
        row.data.find(
          (item) =>
            item.columnName === col.name &&
            item.displayValue !== null &&
            item.displayValue !== undefined,
        ),
      );
      if (nonEmptyCell) {
        nonEmptyColumns.push(col);
      }
    });

    setColumns(nonEmptyColumns);
    setRowData(tableData);
    setModelMeta(newModelMeta);
  };

  useEffect(() => {
    let cancelled = false;

    const onLoadError = (error: any) => {
      if (cancelled) return;
      setIsLoading(false);
      setColumns([]);
      setModelMeta([]);
      setRowData([]);
      setTotalItemCount(0);
      setHasAnyRowViewPermission(false);
      if (!hasSearchValue) {
        console.error("Table load error", error);
      }
    };

    const onLoadSuccess = (
      data: PagedAnalyticsTableDataDto<IAnalyticsDataItem>,
    ) => {
      if (cancelled) return;
      setIsLoading(false);
      setTotalItemCount(data.totalItemCount);
      setHasAnyRowViewPermission(Boolean(data.hasAnyRowViewPermission));
      transformTableData(data.rows, data.meta.columns, data.meta.model);
    };

    setIsLoading(true);
    api.getUiTableData(
      apiUrl,
      filters,
      pageSearchDetails.searchPhrase,
      pageSearchDetails.pageNumber,
      pageSize,
      otherApiParams,
      onLoadSuccess,
      onLoadError,
    );

    return () => {
      cancelled = true;
    };
    // Parent remounts on filter change; search/page are the in-table triggers.
  }, [pageSearchDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create a KeyValuePair where the Key is the Journey Guid, and
  // the Value is the translated Journey title (or not translated, for ClientSent journeys)
  useEffect(() => {
    setJourneyTitles(journeyFilterTitlesByGuid(journeys, t));
  }, [journeys]);

  const handleSearchChange = (newSearchPhrase: string | null) => {
    // Reset page number too
    setPageSearchDetails({ pageNumber: 1, searchPhrase: newSearchPhrase });
  };

  const handlePageChange = (pageNumber: number) => {
    setPageSearchDetails({ ...pageSearchDetails, pageNumber });
  };

  if (isLoading && totalItemCount === 0 && !hasSearchValue) {
    // Initial load
    if (skeletonColumnCount) {
      return (
        <AnalyticsDataTableSkeleton
          columnCount={skeletonColumnCount}
          rowCount={pageSize}
          showSelectionColumn={Boolean(rowSelection)}
          showActionColumn={Boolean(rowAction)}
        />
      );
    }

    return <AnalyticsLoadingSpinner />;
  }

  const noResultsLoaded = !isLoading && totalItemCount === 0;
  const eligiblePageEmployeeIds = rowSelection
    ? Array.from(
        new Set(
          rowData
            .filter((row) => rowSelection.canSelect(row.rawRow))
            .map((row) => row.employeeId),
        ),
      )
    : [];
  const allEligiblePageRowsSelected =
    eligiblePageEmployeeIds.length > 0 &&
    eligiblePageEmployeeIds.every((id) =>
      rowSelection?.selectedEmployeeIds.has(id),
    );
  const showRowAction =
    Boolean(rowAction) &&
    (!rowAction?.hideWhenNoViewPermission ||
      rowAction.forceVisible ||
      hasAnyRowViewPermission);

  return (
    <>
      <SearchField
        onSearch={handleSearchChange}
        isLoading={isLoading}
        loaderColor={userContext.user.client.accentHexColour}
      />
      {noResultsLoaded && (
        <em className="block text-center italic py-4">
          {t("Pages.Analytics.Common.NoTableData")}
        </em>
      )}
      {!noResultsLoaded && (
        <>
          <div className="relative border rounded-lg mt-2 mb-4">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {rowSelection && (
                      <th
                        className={cx(
                          "w-10 px-3 text-center",
                          eligiblePageEmployeeIds.length === 0 &&
                            "cursor-not-allowed",
                        )}
                        scope="col"
                      >
                        <input
                          type="checkbox"
                          aria-label={t(
                            "Analytics.Widgets.Usage.Nudge.SelectEligibleEmployees",
                            "Select eligible employees on this page",
                          )}
                          checked={allEligiblePageRowsSelected}
                          disabled={eligiblePageEmployeeIds.length === 0}
                          onChange={(event) =>
                            rowSelection.onSelectPage(
                              eligiblePageEmployeeIds,
                              event.target.checked,
                            )
                          }
                          className="rounded border-gray-300 text-primary focus:ring-[color:var(--color-primary)] disabled:cursor-not-allowed"
                        />
                      </th>
                    )}
                    {columns.map((col) => {
                      const classNames = getHorizontalAlignmentClassName(col);
                      return (
                        <th
                          className={cx(
                            classNames,
                            "h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0",
                          )}
                          key={col.name}
                        >
                          {t(col.name)}
                        </th>
                      );
                    })}
                    {showRowAction && rowAction && (
                      <th
                        scope="col"
                        className="h-9 px-6 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        {rowAction.header}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rowData.map((row, rowIx) => (
                    <tr
                      key={`${row.employeeId}_${rowIx}`}
                      className="hover:bg-gray-50"
                    >
                      {rowSelection && (
                        <td
                          className={cx(
                            "w-10 px-3 py-2.5 text-center",
                            !rowSelection.canSelect(row.rawRow) &&
                              "cursor-not-allowed",
                          )}
                        >
                          <input
                            type="checkbox"
                            aria-label={t(
                              "Analytics.Widgets.Usage.Nudge.SelectEmployee",
                              "Select employee",
                            )}
                            checked={
                              rowSelection.canSelect(row.rawRow) &&
                              rowSelection.selectedEmployeeIds.has(
                                row.employeeId,
                              )
                            }
                            disabled={!rowSelection.canSelect(row.rawRow)}
                            onChange={() =>
                              rowSelection.onToggle(row.employeeId)
                            }
                            className="rounded border-gray-300 text-primary focus:ring-[color:var(--color-primary)] disabled:cursor-not-allowed"
                          />
                        </td>
                      )}
                      {columns.map((col) => {
                        const cell = row.data.find(
                          (x) => x.columnName === col.name,
                        );
                        if (!cell) return null;
                        const isEmployeeColumn =
                          col.name ===
                            "Analytics.Widgets.Common.Tables.Columns.FirstName" ||
                          col.name ===
                            "Analytics.Widgets.Common.Tables.Columns.LastName";
                        return (
                          <td
                            key={col.name}
                            className={cx(
                              cell.classNames,
                              "min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0",
                              "text-gray-500",
                            )}
                          >
                            {isEmployeeColumn ? (
                              <UserProfileHoverCard
                                employeeId={row.employeeId.toString()}
                              >
                                {cell.displayValue}
                              </UserProfileHoverCard>
                            ) : (
                              cell.displayValue
                            )}
                          </td>
                        );
                      })}
                      {showRowAction && rowAction && (
                        <td className="min-h-[2.5rem] px-6 py-2.5 text-center text-sm text-gray-500">
                          {rowAction.render(row.rawRow)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {bulkActions && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {bulkActions}
            </div>
          )}

          {totalItemCount > pageSize && (
            <div className="flex flex-row mb-4 mt-4">
              <div>
                <Pagination
                  activePage={pageSearchDetails.pageNumber}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={totalItemCount}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  activeClass="pagination-active-list-item"
                  itemClass="pagination-list-item"
                  itemClassFirst="pagination-first-item"
                  itemClassLast="pagination-last-item"
                />
              </div>
              {isLoading && (
                <div className="pl-4">
                  <TinyLoader
                    colour={userContext.user.client.accentHexColour}
                  />
                  <span className="ml-1 text-gray-500 text-sm">
                    {t("Common.Loading")}...
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AnalyticsDataTable;
